import React, { useState, useEffect, useRef } from 'react';
import { Bell, MessageSquare, X, Briefcase, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdminSavNotifications } from '@/features/admin/hooks/useAdminSavNotifications';
import { useCustomerSavNotifications } from '@/features/customer/hooks/useCustomerSavNotifications';
import { useAllOrgaConfigurations } from '@/shared/hooks/useOrgaConfiguration';
import { SavChat } from '@/models/savChat';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface NotificationItem {
    id: string;
    chat: SavChat;
    type: 'admin' | 'customer';
    date: Date;
}

// Composant interne pour la prévisualisation (toast in-navbar) avec thémification
const NotificationPreview = ({
    notification,
    onClose,
    onClick,
    getOrgaName
}: {
    notification: NotificationItem,
    onClose: () => void,
    onClick: () => void,
    getOrgaName: (id: string) => string | undefined
}) => {


    return (
        <div
            className="absolute top-full right-0 mt-4 w-80 bg-surface shadow-2xl rounded-xl border border-border z-[60] overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300"
            role="alert"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-background-secondary/80 border-b border-border-light backdrop-blur-sm">
                <span className="text-xs font-bold text-foreground flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-primary"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                    </span>
                    Nouveau message {notification.type === 'customer' ? '(Client)' : '(Admin)'}
                </span>
                <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="text-foreground-secondary hover:text-foreground p-1 rounded-full hover:bg-background-tertiary transition-colors"
                >
                    <X size={14} />
                </button>
            </div>

            {/* Contenu clickables */}
            <div
                className="p-4 flex items-start gap-3 cursor-pointer hover:bg-background-secondary transition-colors relative"
                onClick={onClick}
            >
                {/* Barre latérale colorée */}
                <div className="absolute left-0 top-4 bottom-4 w-1 rounded-r-full bg-primary" />

                <div className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center shadow-sm bg-primary/10 text-primary">
                    <MessageSquare className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-bold mb-0.5 tracking-wider truncate text-primary">
                        {notification.type === 'admin'
                            ? getOrgaName(notification.chat.organizationId)
                            : 'CASHLIVE'}
                    </p>
                    <p className="text-sm font-semibold text-foreground truncate">
                        {notification.type === 'admin'
                            ? `${notification.chat.customerName}`
                            : `Support Commande`}
                    </p>
                    <p className="text-xs text-foreground-secondary mt-1 line-clamp-2 leading-relaxed">
                        {notification.chat.lastMessagePreview}
                    </p>
                </div>
            </div>
        </div>
    )
}

export const NotificationBell = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [activeTab, setActiveTab] = useState<'admin' | 'customer'>('admin');
    const lastShownTimestampRef = useRef<number>(0);

    // State animation et preview
    const [isAnimating, setIsAnimating] = useState(false);
    const [previewNotif, setPreviewNotif] = useState<NotificationItem | null>(null);

    // Charger les configurations pour afficher les noms et couleurs d'organisations
    // Cela nous sert aussi à savoir si l'utilisateur est admin de quelque part
    const { configurations } = useAllOrgaConfigurations();
    const isAdmin = configurations.length > 0;

    // Charger les notifications (écouteurs globaux)
    const { unreadChats: adminChats, lastNotification: lastAdminNotif } = useAdminSavNotifications();
    const { unreadChats: customerChats, lastNotification: lastCustomerNotif } = useCustomerSavNotifications();

    // Reset d'animation après interaction
    useEffect(() => {
        if (isOpen) {
            setIsAnimating(false);
            setPreviewNotif(null);

            // Auto-sélection de l'onglet pertinent à l'ouverture
            if (adminChats.length > 0) setActiveTab('admin');
            else if (customerChats.length > 0) setActiveTab('customer');
            else if (isAdmin) setActiveTab('admin');
            else setActiveTab('customer');
        }
    }, [isOpen, adminChats.length, customerChats.length, isAdmin]);

    // Détecter nouvelle notification pour animation et preview
    useEffect(() => {
        const candidates = [
            lastAdminNotif ? { ...lastAdminNotif, type: 'admin' as const } : null,
            lastCustomerNotif ? { ...lastCustomerNotif, type: 'customer' as const } : null
        ].filter(Boolean);

        if (candidates.length === 0) return;

        const latest = candidates.sort((a, b) => b!.timestamp - a!.timestamp)[0];

        if (latest && latest.timestamp > lastShownTimestampRef.current) {
            lastShownTimestampRef.current = latest.timestamp;

            // NE PAS afficher de toast si on est déjà sur la page SAV pertinente
            const isAtAdminSav = location.pathname.includes(`/orga/${latest.chat.organizationId}/admin/sav`);
            const isAtCustomerSav = location.pathname === '/my-orders'; // Le chat client est dans my-orders

            if (latest.type === 'admin' && isAtAdminSav) return;
            if (latest.type === 'customer' && isAtCustomerSav) return;

            const newItem: NotificationItem = {
                id: `${latest.type}-${latest.chat.id}`,
                chat: latest.chat,
                type: latest.type,
                date: new Date(latest.timestamp)
            };

            setIsAnimating(true);

            if (!isOpen) {
                setPreviewNotif(newItem);
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    setPreviewNotif(null);
                    setIsAnimating(false);
                }, 4000);
            }
        }
    }, [lastAdminNotif, lastCustomerNotif, isOpen, location.pathname]);

    // Fermer le dropdown au clic extérieur
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getOrgaName = (id: string) => configurations.find(c => c.organizationId === id)?.name;

    // Combiner toutes les notifications pour la liste globale (pour le count)
    const notifications: NotificationItem[] = [
        ...adminChats.map(c => ({
            id: `admin-${c.id}`,
            chat: c,
            type: 'admin' as const,
            date: c.lastMessageAt
        })),
        ...customerChats.map(c => ({
            id: `customer-${c.id}`,
            chat: c,
            type: 'customer' as const,
            date: c.lastMessageAt
        }))
    ].sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
    });

    const totalCount = notifications.length;

    // Filtrer les notifications selon l'onglet actif ou le rôle
    // Si pas admin, on montre tout (qui devrait être que customer) sans filtre
    // Si admin, on filtre selon activeTab
    const displayedNotifications = isAdmin
        ? notifications.filter(n => n.type === activeTab)
        : notifications;

    const handleNotificationClick = (notification: NotificationItem) => {
        setIsOpen(false);
        setPreviewNotif(null);
        if (notification.type === 'admin') {
            navigate(`/orga/${notification.chat.organizationId}/admin/sav`, {
                state: { selectedChatId: notification.chat.id }
            });
        } else {
            // Rediriger vers la liste des commandes avec l'instruction d'ouvrir ce chat spécifique
            navigate('/my-orders', {
                state: {
                    openChatForOrderId: notification.chat.orderId,
                    openChatId: notification.chat.id
                }
            });
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`relative p-2 rounded-full transition-all duration-300 ${isAnimating
                    ? 'text-white bg-slate-700 ring-2 ring-slate-500 ring-offset-2 ring-offset-slate-900 animate-pulse'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800 focus:outline-none'
                    }`}
                aria-label="Notifications"
            >
                <Bell className={`h-6 w-6 ${isAnimating ? 'origin-top animate-bounce' : ''}`} />

                {totalCount > 0 && (
                    <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 ring-2 ring-slate-900 text-[10px] font-bold text-white flex items-center justify-center transform translate-x-1/4 -translate-y-1/4 shadow-sm">
                        {totalCount > 9 ? '9+' : totalCount}
                    </span>
                )}
            </button>

            {/* Notification Preview */}
            {previewNotif && !isOpen && (
                <NotificationPreview
                    notification={previewNotif}
                    onClose={() => setPreviewNotif(null)}
                    onClick={() => handleNotificationClick(previewNotif)}
                    getOrgaName={getOrgaName}
                />
            )}

            {/* Dropdown List */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-surface shadow-xl rounded-xl ring-1 ring-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">

                    {/* Header / Tabs */}
                    {isAdmin ? (
                        <div className="flex border-b border-border-light bg-background-secondary/50">
                            <button
                                onClick={() => setActiveTab('admin')}
                                className={`flex-1 py-3 text-xs font-semibold text-center border-b-2 transition-colors relative flex items-center justify-center gap-2 ${activeTab === 'admin' ? 'border-primary text-primary' : 'border-transparent text-foreground-secondary hover:text-foreground'}`}
                            >
                                <Briefcase className="h-3.5 w-3.5" />
                                Administration
                                {adminChats.length > 0 && <span className="absolute top-2 right-4 h-2 w-2 rounded-full bg-red-500"></span>}
                            </button>
                            <button
                                onClick={() => setActiveTab('customer')}
                                className={`flex-1 py-3 text-xs font-semibold text-center border-b-2 transition-colors relative flex items-center justify-center gap-2 ${activeTab === 'customer' ? 'border-primary text-primary' : 'border-transparent text-foreground-secondary hover:text-foreground'}`}
                            >
                                <User className="h-3.5 w-3.5" />
                                Client
                                {customerChats.length > 0 && <span className="absolute top-2 right-8 h-2 w-2 rounded-full bg-blue-500"></span>}
                            </button>
                        </div>
                    ) : (
                        <div className="p-3 border-b border-border-light bg-background-secondary/50 flex justify-between items-center backdrop-blur-xl">
                            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
                            {totalCount > 0 && (
                                <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
                                    {totalCount} non lu{totalCount > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="max-h-[28rem] overflow-y-auto custom-scrollbar">
                        {displayedNotifications.length === 0 ? (
                            <div className="p-12 text-center flex flex-col items-center">
                                <div className="bg-background-secondary p-3 rounded-full mb-3">
                                    <Bell className="h-6 w-6 text-foreground-secondary" />
                                </div>
                                <p className="text-sm font-medium text-foreground">
                                    Aucune notification {isAdmin && (activeTab === 'admin' ? 'admin' : 'client')}
                                </p>
                                <p className="text-xs text-foreground-secondary mt-1">
                                    Vous êtes à jour !
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border-light">
                                {displayedNotifications.map((notif) => {

                                    return (
                                        <button
                                            key={notif.id}
                                            onClick={() => handleNotificationClick(notif)}
                                            className="w-full text-left p-4 hover:bg-background-secondary transition-all flex items-start gap-4 border-none bg-surface group relative"
                                        >
                                            {/* Indicateur de couleur (barre gauche) */}
                                            <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-r-full group-hover:w-1 transition-all bg-primary" />

                                            <div className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200 bg-primary/10 text-primary">
                                                <MessageSquare className="h-5 w-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] font-bold truncate uppercase mt-0.5 mb-1 tracking-wider text-primary">
                                                    {notif.type === 'admin'
                                                        ? getOrgaName(notif.chat.organizationId)
                                                        : 'CASHLIVE'}
                                                </p>
                                                <div className="flex justify-between items-start gap-2">
                                                    <p className="text-sm font-semibold text-foreground truncate">
                                                        {notif.type === 'admin'
                                                            ? `${notif.chat.customerName}`
                                                            : `Support Commande`}
                                                    </p>
                                                    <span className="text-[10px] text-foreground-secondary whitespace-nowrap flex-shrink-0">
                                                        {formatDistanceToNow(new Date(notif.date), { addSuffix: true, locale: fr })}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-foreground-secondary truncate mt-1 group-hover:text-foreground transition-colors">
                                                    {notif.chat.lastMessagePreview}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
